/**
 * @fileoverview Single editable row inside ParameterEditor — one report parameter's
 *             label, type, mode, required flag, and default value/binding hints.
 * @module ui-admin/components/ParameterEditor/ParameterRow
 *
 * Responsibilities: - Render the per-parameter edit card matching the editor mockup
 *                     (§4.1): index + reorder + delete chrome, label input, type /
 *                     mode selects, required checkbox, and type-conditional fields
 *                     (lookup category, multi-select, default token / default value).
 * Dependencies:     - React, lucide-react, @firesquirrel/core-domain (types),
 *                     ./relativeDateTokens, ./parameterValidators.
 * Architecture:     - Presentation-only, controlled. The parent ParameterEditor owns
 *                     the array and persists via the host's service.
 * Notes:            - Default Token is shown for relative-mode date/dateRange params
 *                     (used for preview/schedule defaults); Default Value input is
 *                     shown for fixed-mode params.
 */

import React from 'react';
import { ArrowUp, ArrowDown, Trash2 } from 'lucide-react';
import type { ReportParameter, ParameterType, RelativeDateToken } from '@firesquirrel/core-domain';
import { RELATIVE_DATE_TOKEN_OPTIONS } from './relativeDateTokens';
import { defaultModeForType } from './parameterValidators';

/** @contract PARAMETER_TYPE_OPTIONS — labelled options for the Type select. */
export const PARAMETER_TYPE_OPTIONS: Array<{ type: ParameterType; label: string }> = [
  { type: 'date', label: 'Date' },
  { type: 'dateRange', label: 'Date Range' },
  { type: 'lookup', label: 'Lookup Value' },
  { type: 'inspectionType', label: 'Inspection Type' },
  { type: 'entity', label: 'Entity' },
  { type: 'account', label: 'Account' },
  { type: 'text', label: 'Text' },
  { type: 'number', label: 'Number' },
  { type: 'boolean', label: 'Boolean' },
];

const RELATIVE_CAPABLE: ReadonlySet<ParameterType> = new Set(['date', 'dateRange']);
const VALID_TOKENS: ReadonlySet<string> = new Set(RELATIVE_DATE_TOKEN_OPTIONS.map((o) => o.token));

/**
 * @contract ParameterRowProps
 * Purpose: Props for one parameter edit row.
 * Fields:
 * - parameter: the controlled ReportParameter being edited.
 * - index: position in the parameters array (display + move guards).
 * - total: array length (move-down guard).
 * - lookupTypeOptions: available lookup categories (host supplies from its
 *   local-first lookup service — never hardcoded).
 * - errors: validation messages scoped to this parameter (optional).
 * - onChange: emit a patched ReportParameter.
 * - onRemove / onMove: array ops delegated to the parent.
 */
export interface ParameterRowProps {
  parameter: ReportParameter;
  index: number;
  total: number;
  lookupTypeOptions?: string[];
  /** Available report fields for the "Filter Field" binding (host supplies). */
  fieldOptions?: Array<{ field: string; label: string }>;
  errors?: string[];
  onChange: (next: ReportParameter) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
}

const inputCls =
  'w-full rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-800 focus:border-orange-400 focus:outline-none';
const selectCls = inputCls;
const labelCls = 'text-[11px] font-medium text-gray-500';

/**
 * @component ParameterRow
 * Purpose:     Edit one report parameter inside ParameterEditor.
 * Props:       ParameterRowProps.
 * Behavior:    Type change resets mode to the type's sensible default and clears
 *              incompatible options; relative-capable types expose the Mode
 *              select and Default Token picker.
 * Data Source: none (controlled).
 * Constraints: presentation-only; validation messages are passed in.
 */
export function ParameterRow({
  parameter, index, total, lookupTypeOptions, fieldOptions, errors, onChange, onRemove, onMove,
}: ParameterRowProps) {
  const relativeCapable = RELATIVE_CAPABLE.has(parameter.type);

  const patch = (next: Partial<ReportParameter>) => onChange({ ...parameter, ...next });

  // Keep defaultValue consistent with mode: relative mode requires a token
  // (a stale value would fail shared validation while the picker masked it by
  // showing its first option); fixed mode drops a leftover token.
  const reconcileDefault = (mode: ReportParameter['mode']): Pick<ReportParameter, 'defaultValue'> => {
    const dv = parameter.defaultValue;
    if (mode === 'relative' && (typeof dv !== 'string' || !VALID_TOKENS.has(dv))) {
      return { defaultValue: 'previousMonth' };
    }
    if (mode === 'fixed' && typeof dv === 'string' && VALID_TOKENS.has(dv)) {
      return { defaultValue: undefined };
    }
    return {};
  };

  const handleTypeChange = (type: ParameterType) => {
    const mode = defaultModeForType(type);
    const options = { ...(parameter.options ?? {}) };
    if (type !== 'lookup') delete options.lookupType;
    if (type !== 'entity' && type !== 'account') delete options.entityCategory;
    onChange({ ...parameter, type, mode, options, ...reconcileDefault(mode) });
  };

  return (
    <div
      data-testid={`parameter-row-${parameter.parameterId}`}
      className="rounded-md border border-gray-200 bg-white p-3"
    >
      <div className="mb-2 flex items-center gap-2">
        <span className="text-xs font-semibold text-gray-600">
          {index + 1}. {parameter.label || 'Untitled'}
          <span className="ml-1 font-mono text-[10px] text-gray-400">({parameter.parameterId})</span>
        </span>
        <span className="flex-1" />
        <button type="button" aria-label="Move parameter up" disabled={index === 0}
          onClick={() => onMove(-1)}
          className="rounded-sm p-1 text-gray-400 hover:bg-gray-100 disabled:text-gray-200">
          <ArrowUp className="h-3.5 w-3.5" />
        </button>
        <button type="button" aria-label="Move parameter down" disabled={index === total - 1}
          onClick={() => onMove(1)}
          className="rounded-sm p-1 text-gray-400 hover:bg-gray-100 disabled:text-gray-200">
          <ArrowDown className="h-3.5 w-3.5" />
        </button>
        <button type="button" aria-label="Delete parameter"
          onClick={onRemove}
          className="rounded-sm p-1 text-gray-400 hover:bg-red-50 hover:text-red-600">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className="col-span-2 flex flex-col gap-0.5">
          <span className={labelCls}>Label</span>
          <input
            type="text"
            aria-label="Parameter label"
            value={parameter.label}
            onChange={(e) => patch({ label: e.target.value })}
            className={inputCls}
          />
        </label>

        <label className="flex flex-col gap-0.5">
          <span className={labelCls}>Type</span>
          <select
            aria-label="Parameter type"
            value={parameter.type}
            onChange={(e) => handleTypeChange(e.target.value as ParameterType)}
            className={selectCls}
          >
            {PARAMETER_TYPE_OPTIONS.map((o) => (
              <option key={o.type} value={o.type}>{o.label}</option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-0.5">
          <span className={labelCls}>Mode</span>
          <select
            aria-label="Parameter mode"
            value={relativeCapable ? parameter.mode : 'fixed'}
            disabled={!relativeCapable}
            onChange={(e) => {
              const mode = e.target.value as ReportParameter['mode'];
              onChange({ ...parameter, mode, ...reconcileDefault(mode) });
            }}
            className={`${selectCls} disabled:bg-gray-50 disabled:text-gray-400`}
          >
            <option value="fixed">Fixed</option>
            {relativeCapable && <option value="relative">Relative</option>}
          </select>
        </label>

        <label className="col-span-2 flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            aria-label="Required parameter"
            checked={parameter.required}
            onChange={(e) => patch({ required: e.target.checked })}
          />
          Required
        </label>

        <label className="col-span-2 flex flex-col gap-0.5">
          <span className={labelCls}>Filter Field (the report field this parameter filters)</span>
          {fieldOptions && fieldOptions.length > 0 ? (
            <select
              aria-label="Filter field"
              value={parameter.filterField ?? ''}
              onChange={(e) => patch({ filterField: e.target.value || undefined })}
              className={selectCls}
            >
              <option value="">— not bound (value is ignored at run time) —</option>
              {fieldOptions.map((f) => (
                <option key={f.field} value={f.field}>{f.label} ({f.field})</option>
              ))}
              {parameter.filterField && !fieldOptions.some((f) => f.field === parameter.filterField) && (
                <option value={parameter.filterField}>
                  {parameter.filterField} — not in this report's data source
                </option>
              )}
            </select>
          ) : (
            <input
              type="text"
              aria-label="Filter field"
              placeholder="e.g. contacts.createdAt"
              value={parameter.filterField ?? ''}
              onChange={(e) => patch({ filterField: e.target.value || undefined })}
              className={inputCls}
            />
          )}
        </label>

        {parameter.type === 'lookup' && (
          <>
            <label className="col-span-2 flex flex-col gap-0.5">
              <span className={labelCls}>Lookup Category (from Dexie lookup_values)</span>
              <select
                aria-label="Lookup category"
                value={parameter.options?.lookupType ?? ''}
                onChange={(e) => patch({ options: { ...parameter.options, lookupType: e.target.value || undefined } })}
                className={selectCls}
              >
                <option value="">— choose a category —</option>
                {(lookupTypeOptions ?? []).map((lt) => (
                  <option key={lt} value={lt}>{lt}</option>
                ))}
              </select>
            </label>
            <label className="col-span-2 flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                aria-label="Allow multi-select"
                checked={parameter.options?.multi ?? false}
                onChange={(e) => patch({ options: { ...parameter.options, multi: e.target.checked } })}
              />
              Allow multi-select
            </label>
          </>
        )}

        {relativeCapable && parameter.mode === 'relative' && (
          <label className="col-span-2 flex flex-col gap-0.5">
            <span className={labelCls}>Default Token (for preview/schedules)</span>
            <select
              aria-label="Default token"
              value={(parameter.defaultValue as RelativeDateToken | undefined) ?? 'previousMonth'}
              onChange={(e) => patch({ defaultValue: e.target.value as RelativeDateToken })}
              className={selectCls}
            >
              {RELATIVE_DATE_TOKEN_OPTIONS.map((o) => (
                <option key={o.token} value={o.token}>{o.label}</option>
              ))}
            </select>
          </label>
        )}

        {parameter.mode === 'fixed' && parameter.type !== 'lookup' && (
          <label className="col-span-2 flex flex-col gap-0.5">
            <span className={labelCls}>Default Value (optional)</span>
            <input
              type="text"
              aria-label="Default value"
              value={typeof parameter.defaultValue === 'string' ? parameter.defaultValue : ''}
              onChange={(e) => patch({ defaultValue: e.target.value || undefined })}
              className={inputCls}
            />
          </label>
        )}
      </div>

      {errors && errors.length > 0 && (
        <div role="alert" className="mt-2 text-[11px] text-red-600">
          {errors.map((err) => <div key={err}>{err}</div>)}
        </div>
      )}
    </div>
  );
}
